import { useLayoutEffect, useMemo, useRef } from 'react';
import type { DrawingPublic, Phase, RoundPublic } from '@listen-draw/shared';
import { useServerNow } from '../lib/time.js';
import { Countdown } from './Countdown.js';
import { t, useLocale } from '../lib/i18n.js';

interface Props {
  round: RoundPublic | null;
  phase: Phase;
  mini?: boolean;
  localClock?: boolean;
  /** スタンプ表示（回答した選択肢、CORRECT など） */
  stamp?: { text: string; kind: 'pick' | 'ok' | 'muted' } | null;
}

/** 経過時間（pause 補正後、ms） */
export function drawingElapsed(d: DrawingPublic, now: number, complete: boolean): number {
  if (complete) return d.durationMs;
  const effective = d.pausedAt ?? now;
  return Math.max(0, Math.min(d.durationMs, effective - d.startAt - d.pausedTotalMs));
}

export function drawingPct(d: DrawingPublic | undefined, now: number, complete: boolean): number {
  if (!d) return 0;
  return Math.round((drawingElapsed(d, now, complete) / d.durationMs) * 100);
}

const ACTIVE: Phase[] = ['drawing', 'final'];

export function Canvas({ round, phase, mini, stamp, localClock }: Props) {
  useLocale();
  const drawing = round?.drawing;
  const complete = phase === 'reveal' || phase === 'results';
  const now = useServerNow(
    ACTIVE.includes(phase) && !!drawing && drawing.pausedAt === null,
    60,
    localClock,
  );
  const elapsed = drawing ? drawingElapsed(drawing, now, complete) : 0;
  const pct = drawing ? Math.round((elapsed / drawing.durationMs) * 100) : 0;
  const svgRef = useRef<SVGSVGElement>(null);
  const lengths = useRef(new Map<string, number>());

  const strokes = useMemo(() => {
    if (!drawing) return [];
    return drawing.stages.flatMap((st) =>
      st.strokes.map((s, i) => ({
        ...s,
        key: `${st.index}-${i}`,
        startMs: st.startMs,
        endMs: st.endMs,
        stage: st.index,
      })),
    );
  }, [drawing]);

  // 段階内でストローク長に比例して描き進める
  useLayoutEffect(() => {
    const svg = svgRef.current;
    if (!svg || !drawing) return;
    const byStage = new Map<
      number,
      { key: string; len: number; el: SVGPathElement; fill: string }[]
    >();
    for (const path of svg.querySelectorAll<SVGPathElement>('path[data-key]')) {
      const key = path.dataset.key!;
      const stage = Number(path.dataset.stage);
      const geometry = path.getAttribute('d') ?? key;
      let len = lengths.current.get(geometry);
      if (len === undefined) {
        len = path.getTotalLength();
        lengths.current.set(geometry, len);
      }
      const arr = byStage.get(stage) ?? [];
      arr.push({ key, len, el: path, fill: path.dataset.fillColor ?? 'none' });
      byStage.set(stage, arr);
    }
    for (const st of drawing.stages) {
      const items = byStage.get(st.index) ?? [];
      const total = items.reduce((a, b) => a + b.len, 0) || 1;
      const span = Math.max(1, st.endMs - st.startMs);
      const frac = Math.max(0, Math.min(1, (elapsed - st.startMs) / span));
      let budget = frac * total;
      for (const it of items) {
        const drawn = Math.max(0, Math.min(it.len, budget));
        budget -= it.len;
        it.el.style.strokeDasharray = `${it.len}`;
        it.el.style.strokeDashoffset = `${it.len - drawn}`;
        it.el.setAttribute('fill', drawn >= it.len - 0.5 ? it.fill : 'none');
      }
    }
  }, [drawing, elapsed, strokes]);

  const showCountdown =
    round?.countdown && (phase === 'countdown_audio' || phase === 'countdown_draw');
  const preparing = phase === 'preparing';

  return (
    <div
      className={`canvas-wrap${mini ? ' canvas-wrap--mini' : ''}`}
      aria-label={t('Sketch canvas', '線画キャンバス')}
    >
      {!mini && <span className="tag">{t('The sketch', 'スケッチ')}</span>}
      {!mini && drawing && <span className="pct num">{pct}%</span>}
      <svg
        ref={svgRef}
        viewBox="0 0 400 400"
        role="img"
        aria-label={t('Sketch appearing line by line', '少しずつ現れる線画')}
      >
        {strokes.map((s) => (
          <path
            key={s.key}
            data-key={s.key}
            data-stage={s.stage}
            data-fill-color={s.fillColor ?? (s.fill ? 'currentColor' : 'none')}
            d={s.d}
            transform={s.transform}
            fill="none"
            stroke={s.color ?? 'currentColor'}
            strokeWidth={s.w}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ strokeDasharray: 1, strokeDashoffset: 1 }}
          />
        ))}
      </svg>
      {showCountdown && round?.countdown && (
        <Countdown countdown={round.countdown} compact={mini} />
      )}
      {preparing && (
        <div className="preparing">
          <div className="stack center">
            <div className="spinner" style={{ margin: '0 auto' }} />
            <div>{t('Getting the sketch ready…', '線画を準備中…')}</div>
          </div>
        </div>
      )}
      {stamp && (
        <div
          className={`stamp${stamp.kind === 'ok' ? ' stamp--ok' : stamp.kind === 'muted' ? ' stamp--muted' : ''}${mini ? ' stamp--small' : ''}`}
        >
          <span>{stamp.text}</span>
        </div>
      )}
    </div>
  );
}
